/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @file EnrollmentInformation.jsx
 * @author Anuja Gaikwad
 * @since 1.4.9
 */

import React from 'react';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import CustomCard from './CustomCard';
import { BadgeTemplate } from '../tabular/Tabular';
import mixins from '../../assets/styles/mixins.scss';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import ConfirmationCard from './ConfirmationCard'; // Ensure correct path

const styles = () => ({
  card: {
    display: 'flex',
    minHeight: '200px',
    padding: 16,
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    alignSelf: 'stretch',
    borderRadius: '16px',
    background: mixins.chalkWhiteFont
  },

  scrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    '& .MuiChip-labelSmall, & .MuiChip-label': {
      paddingLeft: 0,
      paddingRight: 0
    }
  },

  title: {
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '28px',
    letterSpacing: '-0.4px'
  },

  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%'
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },

  label: {
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight22,
    fontFamily: mixins.fontFamilyPlusSans
  },

  value: {
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans
  },

  dropdownWrapper: {
    minWidth: 120
  },
  filterPopover: {
    marginTop: 0
  },
  chip: {
    padding: '2px 6px'
  }
});

function EnrollmentInformation({
  item,
  isConfirmationCardOpen,
  setIsConfirmationCardOpen,
  textInfo,
  handleConfirmDelete,
  classes
}) {
  const hasValue = (value) => value?.toString().trim();
  const normalizeKey = (val) => String(val || '').toLowerCase();

  const renderRow = ({ label, value, isBadge = false }) => {
    if (!hasValue(value)) {
      return null;
    }

    return (
      <div className={classes.row}>
        <Typography className={classes.label}>
          {label}
        </Typography>

        {isBadge ? (
          <BadgeTemplate
            label={value}
            status={normalizeKey(value)}
            showIcon={false}
            classes={{ badge: classes.chip }}
          />
        ) : (
          <Typography className={classes.value}>
            {value}
          </Typography>
        )}
      </div>
    );
  };

  return (
    <CustomCard className={classes.card}>
      <CustomScrollbar autoHeight>
        <div className={classes.scrollContainer}>
          <Typography className={classes.title}>
            {textInfo.enrolmentCardTitle}
          </Typography>

          <div className={classes.contentWrapper}>
            {renderRow({
              label: textInfo.statusLabel,
              value: item.status,
              isBadge: true
            })}

            {renderRow({
              label: textInfo.enrolledLabel,
              value: item.enrolledDate
            })}

            {renderRow({
              label: textInfo.accessLabel,
              value: item.access
            })}

            {renderRow({
              label: textInfo.roleLabel,
              value: item.role,
              isBadge: true
            })}
          </div>
        </div>
      </CustomScrollbar>

      {/* Confirmation Modal */}
      <ConfirmationCard
        open={isConfirmationCardOpen}
        onClose={() => setIsConfirmationCardOpen(false)}
        title={textInfo?.title}
        content={textInfo?.content}
        cancelText={textInfo?.cancelText}
        confirmText={textInfo?.deleteConfirmationConfirmText}
        onCancel={() => setIsConfirmationCardOpen(false)}
        onConfirm={handleConfirmDelete}
        showCloseButton
        classes={{
          paper: classes.paper,
          titleText: classes.titleText,
          contentText: classes.contentText,
          cancelButtonText: classes.cancelButtonText,
          confirmButtonText: classes.confirmButtonText,
          confirmButton: classes.confirmButton,
          cancelButton: classes.cancelButton,
          actions: classes.actions
        }}
      />
    </CustomCard>
  );
}

EnrollmentInformation.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    enrolledDate: PropTypes.string,
    access: PropTypes.string,
    role: PropTypes.string
  }),
  isConfirmationCardOpen: PropTypes.bool,
  setIsConfirmationCardOpen: PropTypes.func,
  textInfo: PropTypes.shape({
    deleteConfirmationConfirmText: PropTypes.string,
    enrolmentCardTitle: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    cancelText: PropTypes.string,
    statusLabel: PropTypes.string,
    enrolledLabel: PropTypes.string,
    accessLabel: PropTypes.string,
    roleLabel: PropTypes.string
  }),
  handleConfirmDelete: PropTypes.func,
  classes: PropTypes.object.isRequired
};

EnrollmentInformation.defaultProps = {
  item: {
    id: '',
    status: '',
    enrolledDate: '',
    access: '',
    role: ''
  },
  isConfirmationCardOpen: false,
  setIsConfirmationCardOpen: () => {},
  textInfo: {
    deleteConfirmationConfirmText: '',
    enrolmentCardTitle: '',
    title: '',
    content: '',
    cancelText: '',
    statusLabel: '',
    enrolledLabel: '',
    accessLabel: '',
    roleLabel: ''
  },
  handleConfirmDelete: () => {}
};

export default withStyles(styles)(EnrollmentInformation);
